import { ClearOutlined, CloseOutlined, CloudDownloadOutlined, FilePdfOutlined, PrinterOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { App, Button, Descriptions, Empty, Flex, Space } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { validate as validateUuid } from 'uuid';
import { MainContent } from '../components/MainContent.tsx';
import { PdfPreviewModal } from '../components/PdfPreviewModal.tsx';
import { TestTubeColor } from '../components/TestTubeColor.tsx';
import { type Label, useScannedLabelsStore } from '../hooks/store/useScannedLabelsStore.ts';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner.ts';
import { printErrorShown, usePrinter } from '../hooks/usePrinter.ts';
import { PrintButton } from '../PrintButton.tsx';
import { ellipsis } from '../utils/ellipsis.ts';
import { testTubeColorProperties } from '../utils/enumHelpers.ts';
import { saveUrl } from '../utils/helpers.ts';
import { patientFullName } from '../utils/patient.ts';
import { RequestDetailsModal } from './request/RequestDetailsModal.tsx';
import { MARK_LABEL_PRINTED_MUTATION, ORDER_LABEL_BY_UUID_QUERY } from './SampleCollection.gql';
import { Content } from './sample-collection/Content.tsx';
import { generateReport } from './sample-collection/report.ts';

const SampleCollection: React.FC = () => {
  const [detailsRequestId, setDetailsRequestId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const apolloClient = useApolloClient();
  const { notification, message } = App.useApp();
  const auth = useAuth();
  const { scannedOrders, setScannedOrders, scannedLabelIds, setScannedLabelIds } = useScannedLabelsStore();

  const [markLabelPrintedMutation] = useMutation(MARK_LABEL_PRINTED_MUTATION);

  const queryByUuid = async (uuid: string) => {
    if (scannedLabelIds.includes(uuid)) {
      notification.info({
        title: 'Das Etikett wurde bereits eingescannt',
      });
      return;
    }

    const { data, error } = await apolloClient.query({
      query: ORDER_LABEL_BY_UUID_QUERY,
      variables: {
        id: uuid,
      },
    });

    if (error || !data?.orderLabel) {
      notification.warning({
        title: 'Das Etikett konnte nicht gefunden werden',
        description: `Etiketten-ID: ${uuid}`,
      });
      return;
    }

    const scannedOrderIdx = scannedOrders.findIndex(it => it.id === data.orderLabel?.order.id);
    if (scannedOrderIdx === -1) {
      setScannedOrders([...scannedOrders, { ...data.orderLabel!.order, availableLabels: 1 }]);
    } else {
      scannedOrders.splice(scannedOrderIdx, 1, {
        ...scannedOrders[scannedOrderIdx],
        availableLabels: scannedOrders[scannedOrderIdx].availableLabels + 1,
      });
      setScannedOrders(scannedOrders);
    }
    setScannedLabelIds([...scannedLabelIds, uuid]);

    const patientData = data.orderLabel.order.request.patientData;
    notification.success({
      style: { width: '500px' },
      title: data.orderLabel.name,
      description: (
        <Descriptions column={1} styles={{ label: { width: '80px' } }} size="small" bordered>
          <Descriptions.Item label="Farbe">
            <TestTubeColor properties={testTubeColorProperties(data.orderLabel.color)} />
          </Descriptions.Item>
          <Descriptions.Item label="Auftrag">
            <span className={monoFont}>{data.orderLabel.order.number}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Patient">{patientFullName(patientData)}</Descriptions.Item>
          <Descriptions.Item label="Zuweiser">{data.orderLabel.order.request.doctor.name}</Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  useBarcodeScanner({
    validate: validateUuid,
    onScan: queryByUuid,
    onError: v => notification.error({ title: 'Fehler', description: `Ungültiger Wert: ${ellipsis(v, 100)}` }),
    handlePaste: true,
  });

  const cleanup = useCallback(() => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl('');
  }, [pdfUrl]);

  const { canPrint, print } = usePrinter();

  const printLabel = async (label: Label) => {
    try {
      const response = await fetch(`${window._env_.API_URL}/rest/order-label/${label.id}/download`, {
        headers: {
          authorization: `Bearer ${auth.user?.access_token}`,
        },
      });
      const buffer = await response.blob().then(it => it.arrayBuffer());
      if (buffer.byteLength > 0) {
        await print(buffer, label.specialPrinterRequired ? 'special-label' : 'label', { title: 'Etikett' });
      }
    } catch (e) {
      if (!printErrorShown(e)) {
        message.error('Beim Drucken ist ein Fehler aufgetreten');
      }
      return;
    }

    message.success('Druckauftrag erstellt');

    try {
      await markLabelPrintedMutation({
        variables: {
          id: label.id,
        },
      });
    } catch (_e) {
      message.error('Der Druckstatus kann nicht gesetzt werden');
    }
  };

  const printReport = async () => {
    if (!pdfUrl) {
      return;
    }

    try {
      const response = await fetch(pdfUrl);
      if (response.ok) {
        const buffer = await response.blob().then(it => it.arrayBuffer());
        await print(buffer, 'a4', { title: 'Probenannahme Report' });
        message.success('Dokument wurde an den Drucker gesendet');
      } else {
        message.error('Beim Drucken ist ein Fehler aufgetreten');
      }
    } catch (e) {
      if (!printErrorShown(e)) {
        message.error('Beim Drucken ist ein Fehler aufgetreten');
      }
    }
  };

  return (
    <MainContent size="large">
      <PageHeader
        title="Probenannahme"
        style={{ padding: 0, paddingBottom: 'inherit' }}
        extra={
          scannedOrders.length ? (
            <Flex gap="small">
              <Button
                onClick={async () => {
                  try {
                    setPdfUrl(await generateReport(scannedOrders, scannedLabelIds, auth.user));
                  } catch (_error) {
                    message.error('Der Report konnte nicht erstellt werden');
                  }
                }}
                icon={<FilePdfOutlined />}
              >
                Report
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={() => {
                  setScannedOrders([]);
                  setScannedLabelIds([]);
                }}
              >
                Liste leeren
              </Button>
            </Flex>
          ) : null
        }
      />

      {!scannedOrders.length && <Empty description="Bitte die QR-Codes auf den Etiketten scannen" />}
      <Content
        setDetailsRequestId={setDetailsRequestId}
        printLabel={printLabel}
        setScannedLabelIds={setScannedLabelIds}
        scannedLabelIds={scannedLabelIds}
        setScannedOrders={setScannedOrders}
        scannedOrders={scannedOrders}
      />
      <RequestDetailsModal requestId={detailsRequestId} onClose={() => setDetailsRequestId(null)} />
      <PdfPreviewModal
        title="Probenannahme Report"
        url={pdfUrl}
        loading={false}
        footer={
          <Space orientation="horizontal">
            <PrintButton type="primary" icon={<PrinterOutlined />} onClick={printReport}>
              Drucken
            </PrintButton>
            <Button
              type={!canPrint ? 'primary' : undefined}
              icon={<CloudDownloadOutlined />}
              onClick={() =>
                saveUrl(pdfUrl, `probenannahme-report-${dayjs().format('DD_MM_YYYY_HH_mm')}.pdf`, () =>
                  message.error('Datei konnte nicht heruntergeladen werden')
                )
              }
            >
              Download
            </Button>
            <Button icon={<CloseOutlined />} onClick={cleanup}>
              Schließen
            </Button>
          </Space>
        }
        onClose={cleanup}
      />
    </MainContent>
  );
};

export default SampleCollection;
